import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';

interface TimeSlot {
  time: number;
  hour: string;
  available: boolean;
  bookingCount: number;
}

interface DayAvailability {
  date: string;
  slots: TimeSlot[];
  totalAvailable: number;
}

interface TimeSlotPickerProps {
  value: number | null;
  onChange: (timestamp: number | null) => void;
  minDate?: Date;
  serviceTypeId?: string;
}

export function TimeSlotPicker({ value, onChange, minDate, serviceTypeId }: TimeSlotPickerProps) {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [availability, setAvailability] = useState<DayAvailability | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchAvailability = async () => {
      setIsLoading(true);
      try {
        // Build URL with optional serviceTypeId for service-dependent slot duration
        let url = `/api/bookings/availability?date=${selectedDate}`;
        if (serviceTypeId) {
          url += `&serviceTypeId=${serviceTypeId}`;
        }
        const response = await api.get<DayAvailability>(url);
        setAvailability(response);
      } catch (error) {
        console.error('Availability fetch error:', error);
        setAvailability(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAvailability();
  }, [selectedDate, serviceTypeId]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    onChange(null); // Reset time selection when date changes
  };

  const handleSlotSelect = (slot: TimeSlot) => {
    if (slot.available) {
      onChange(slot.time);
    }
  };

  const formatSelectedDateTime = () => {
    if (!value) return '';
    const date = new Date(value * 1000);
    return date.toLocaleString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-4">
      {/* Date Picker */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t('booking.date')}
        </label>
        <input
          type="date"
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          value={selectedDate}
          onChange={handleDateChange}
          min={minDate?.toISOString().split('T')[0]}
        />
      </div>

      {/* Time Slots */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          {t('booking.time')}
        </label>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
          </div>
        ) : availability?.slots.length === 0 ? (
          <p className="text-sm text-gray-500 py-4">
            No hay horarios disponibles para esta fecha (cerrado).
          </p>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-2">
            {availability?.slots.map((slot) => (
              <button
                key={slot.time}
                type="button"
                disabled={!slot.available}
                onClick={() => handleSlotSelect(slot)}
                className={`
                  px-3 py-2 text-sm font-medium rounded-md transition-colors
                  ${
                    value === slot.time
                      ? 'bg-blue-600 text-white'
                      : slot.available
                      ? 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                  }
                `}
              >
                {slot.hour}
              </button>
            ))}
          </div>
        )}

        {/* Availability Legend */}
        <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <span className="h-3 w-3 rounded bg-gray-100" />
            <span>Disponible</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="h-3 w-3 rounded bg-gray-50" />
            <span>No disponible</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="h-3 w-3 rounded bg-blue-600" />
            <span>Seleccionado</span>
          </div>
        </div>
      </div>

      {/* Selected DateTime Display */}
      {value && (
        <div className="rounded-md bg-blue-50 px-4 py-3">
          <p className="text-sm font-medium text-blue-800">
            Cita seleccionada: {formatSelectedDateTime()}
          </p>
        </div>
      )}
    </div>
  );
}
